if (!customElements.get('product-info')) {
  customElements.define('product-info', class ProductInfo extends HTMLElement {
    constructor() {
      super();
      this.input = this.querySelector('.quantity__input');
      this.currentVariant = this.querySelector('.product-variant-id');
      this.variantSelects = this.querySelector('variant-radios')
      this.submitButton = this.querySelector('[type="submit"]');
    }

    cartUpdateUnsubscriber = undefined;
    variantChangeUnsubscriber = undefined;

    connectedCallback() {
      if (!this.input) return;
      this.quantityForm = this.querySelector('.product-form__quantity');
      if (!this.quantityForm) return;
      this.setQuantityBoundries();  
      if (!this.dataset.originalSection) {
        this.cartUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, this.fetchQuantityRules.bind(this));
      }
      this.variantChangeUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
        const sectionId = this.dataset.originalSection ? this.dataset.originalSection : this.dataset.section;
        if (event.data.sectionId !== sectionId) return;
        this.updateQuantityRules(event.data.sectionId, event.data.html);
        this.setQuantityBoundries();
      });
    }

    disconnectedCallback() {
      if (this.cartUpdateUnsubscriber) {
        this.cartUpdateUnsubscriber();
      }
      if (this.variantChangeUnsubscriber) {
        this.variantChangeUnsubscriber();
      }
    }

    setQuantityBoundries() {
      const data = {
        cartQuantity: this.input.dataset.cartQuantity ? parseInt(this.input.dataset.cartQuantity) : 0,
        min: this.input.dataset.min ? parseInt(this.input.dataset.min) : 1,
        max: this.input.dataset.max ? parseInt(this.input.dataset.max) : null,
        step: this.input.step ? parseInt(this.input.step) : 1
      }

      let min = data.min;
      const max = data.max === null ? data.max : data.max - data.cartQuantity;
      if (max !== null) min = Math.min(min, max);
      if (data.cartQuantity >= data.min) min = Math.min(min, data.step);

      this.input.min = min;
      this.input.max = max;
      this.input.value = min;
      publish(PUB_SUB_EVENTS.quantityUpdate, undefined);  
    }

    fetchQuantityRules() {
      if (!this.currentVariant || !this.currentVariant.value) return;
      this.querySelector('.quantity__rules-cart .loading-overlay').classList.remove('hidden');
      fetch(`${this.dataset.url}?variant=${this.currentVariant.value}&section_id=${this.dataset.section}`).then((response) => {
        return response.text()
      })
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        this.updateQuantityRules(this.dataset.section, html);
        this.setQuantityBoundries();
      })
      .catch(e => {
        console.error(e);
      })
      .finally(() => {
        this.querySelector('.quantity__rules-cart .loading-overlay').classList.add('hidden');
      });
    }

    updateQuantityRules(sectionId, html) {
      const quantityFormUpdated = html.getElementById(`Quantity-Form-${sectionId}`);
      const selectors = ['.quantity__input', '.quantity__rules', '.quantity__label'];
      for (let selector of selectors) { 
        const current = this.quantityForm.querySelector(selector);
        const updated = quantityFormUpdated.querySelector(selector);
        if (!current || !updated) continue;
        if (selector === '.quantity__input') {
          const attributes = ['data-cart-quantity', 'data-min', 'data-max', 'step'];
          for (let attribute of attributes) {
            const valueUpdated = updated.getAttribute(attribute);
            if (valueUpdated !== null) current.setAttribute(attribute, valueUpdated);
          }
        } else {
          current.innerHTML = updated.innerHTML;
        }
      }
    }
  }
)};

// Currency-agnostic price helpers.
// Liquid injects window.PRICE_FMT (shop base currency); Transcy may render
// a different active currency in .transcy-money, so we always re-detect the
// format from the live DOM text and reuse it for output.
const PriceFmt = (function(){
  const fallbackTemplate = { prefix: '', suffix: '', decimalSep: '.', thousandsSep: '', decimals: 2 };

  function detectTemplate(text) {
    if (typeof text !== 'string' || !text.length) return fallbackTemplate;
    const numberMatch = text.match(/-?[\d.,'\s ]+/);
    if (!numberMatch) return fallbackTemplate;
    // Strip leading/trailing whitespace from the matched number. The Liquid
    // template renders "{{ money_price }} " with a trailing space; without
    // this trim it gets captured as part of the number and mis-detected as
    // an extra separator (turning the real decimal comma into thousands).
    const numberPart = numberMatch[0].replace(/^[\s ]+|[\s ]+$/g, '');
    if (!numberPart) return fallbackTemplate;
    const startIdx = text.indexOf(numberPart);
    const prefix = text.slice(0, startIdx);
    const suffix = text.slice(startIdx + numberPart.length);
    const seps = numberPart.match(/[.,'\s ]/g) || [];
    let decimalSep = '';
    let thousandsSep = '';
    let decimals = 0;
    if (seps.length) {
      const lastSep = seps[seps.length - 1];
      const lastIdx = numberPart.lastIndexOf(lastSep);
      const tail = numberPart.slice(lastIdx + 1);
      if (/^\d{1,2}$/.test(tail)) {
        // 1-2 trailing digits → decimal separator (the common case).
        decimalSep = lastSep;
        decimals = tail.length;
      } else if (seps.length > 1) {
        // Multiple separators with 3+ trailing digits → last one is decimal
        // (e.g. "1'234.56"). Output decimals are capped to 2 below.
        decimalSep = lastSep;
        decimals = tail.length;
      }
      // Otherwise (single separator + 3+ trailing digits) → thousands.
      // Shopify renders currencies with at most 2 decimals.
      const others = seps.filter(s => s !== decimalSep);
      if (others.length) thousandsSep = others[0];
      if (!decimalSep && seps.length === 1) thousandsSep = seps[0];
    }
    if (decimals > 2) decimals = 2;
    return { prefix, suffix, decimalSep, thousandsSep, decimals };
  }

  function parse(text) {
    if (typeof text !== 'string') return NaN;
    const tpl = detectTemplate(text);
    let numberPart = text.replace(/[^\d.,'\s -]/g, '');
    if (tpl.thousandsSep) {
      // Strip thousands separator (use a literal split to avoid regex escaping).
      numberPart = numberPart.split(tpl.thousandsSep).join('');
    }
    if (tpl.decimalSep && tpl.decimalSep !== '.') {
      numberPart = numberPart.replace(tpl.decimalSep, '.');
    }
    numberPart = numberPart.replace(/[\s ]/g, '');
    return parseFloat(numberPart);
  }

  function format(amount, template) {
    const tpl = template || fallbackTemplate;
    let decimals = tpl.decimals != null ? tpl.decimals : 2;
    if (decimals > 2) decimals = 2;
    if (decimals < 0) decimals = 0;
    const fixed = Number(amount).toFixed(decimals);
    const parts = fixed.split('.');
    if (tpl.thousandsSep) {
      parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, tpl.thousandsSep);
    }
    const numberOut = decimals > 0 ? parts[0] + (tpl.decimalSep || '.') + parts[1] : parts[0];
    return (tpl.prefix || '') + numberOut + (tpl.suffix || '');
  }

  // Read/write handle for the active single-price text. Transcy may or may
  // not wrap the money in .transcy-money. When absent we target the first
  // text node inside .price-item--regular so sibling elements (e.g. the
  // "(je Stk.)" smalltext span) are preserved on write. There can be more
  // than one .price__regular (the block is cloned above the cart button) —
  // we read from the first and write to every match so all stay in sync.
  function getPriceHandle() {
    const transcyEls = document.querySelectorAll('.price__regular .price-item .transcy-money');
    if (transcyEls.length) {
      return {
        read: () => transcyEls[0].textContent.trim(),
        write: (v) => { transcyEls.forEach(el => { el.textContent = v; }); }
      };
    }
    let items = document.querySelectorAll('.price__regular .price-item--regular');
    if (!items.length) items = document.querySelectorAll('.price__regular .price-item');
    if (!items.length) return null;
    const textNodes = [];
    items.forEach(item => {
      for (const node of item.childNodes) {
        if (node.nodeType === 3 && node.textContent.trim().length) {
          textNodes.push(node);
          break;
        }
      }
    });
    if (!textNodes.length) return null;
    return {
      read: () => textNodes[0].textContent.trim(),
      write: (v) => { textNodes.forEach(node => { node.textContent = v + ' '; }); }
    };
  }

  function templateFromPrice() {
    const h = getPriceHandle();
    return h ? detectTemplate(h.read()) : fallbackTemplate;
  }

  return { parse, format, detectTemplate, getPriceHandle, templateFromPrice };
})();

$(document).ready(function(){
  // === Breite x Höhe
  $('#product_width, #product_height').on('change focusout', function(){
    if ( productWidth === undefined ) {
      productWidth = 0;
    } else if ( Number($('#product_width').val()) < 0 ) {
      console.log('smaller als 0');
      productWidth = 0;
      $('#product_width').val(productWidth);
    } else {
      productWidth = $('#product_width').val();
    }
    
    if ( productHeight === undefined ) {
      productHeight = 0;
    } else if ( Number($('#product_height').val()) < 0 ) {
      console.log('smaller als 0');
      productHeight = 0;
      $('#product_height').val(productHeight);
    } else {
      productHeight = $('#product_height').val();
    }

    // Wenn Maße gesetzt, blende Gesamtpreis ein
    if ( $('#product_width').val() > 0 && $('#product_height').val() > 0 ) {
      $('.finalprice').fadeIn();
    }

    if ( productType == "Emaille Pin" ) {
      productSurface = Number(productWidth * productHeight).toFixed(2);
      console.log('Calculated Input Surface: ' + productSurface);
    } else {
      productSurface = Math.ceil(productWidth * productHeight);
      console.log('Calculated Input Surface: ' + productSurface);
    }

    productSurfaceComma = String(productSurface).replace('.',',');
    $('.surface-result').html(productSurfaceComma);

    // update Variant
    checkSurfaceQuantityToggleVariant();

    // update globo variants
    $('.gpo-form__group :is(input[gpo-data-variant-id],option[gpo-data-variant-id])').each(function(){
      if ( $(this)[0].hasAttribute('gpo-data-variant-id') ) {
        // get product handle of globo option
        let productHandle = $(this).attr('gpo-data-product-handle');
  
        // Get recent surface from addon products variants list and set new variant
        let addonProduct = $('.addon-products-list').find('.addon-product[data-addon-handle='+productHandle+']').find('.addon-variant[data-variant-title='+recentVariantSurface+']');
        let variantID = addonProduct.attr('data-variant-id');
        $(this).attr('gpo-data-variant-id', variantID);
  
        // overwrite label with correct addon price
        const tpl = PriceFmt.templateFromPrice();
        let addonPriceValue = addonProduct.attr('data-variant-price');
        let addonPriceFormatted = PriceFmt.format(parseFloat(addonPriceValue), tpl);
        if ( $(this).is('input:not([type=radio][name*="surface=="])') ) {
          let addonLabel = $(this).next('label').find('.addOn');
          let newLabel = '(+ ' + addonPriceFormatted + ')';
          addonLabel.html(newLabel);
          if ( $(this).is(':checked') ) {
            $(this).parents('.gpo-element:has(:not(input[type=radio][name*="surface=="]))').children('label').find('.addOn .transcy-money').html(addonPriceFormatted);
          }
        } else if ( $(this).is('option') ) {
          let addonLabel = $(this).text();
          let priceRegex = /\(\+\s*[^)]+\)/;
          let newLabel = addonLabel.replace(priceRegex, `(+ ${addonPriceFormatted})`);
          $(this).text(newLabel);
        }
        
        
      }
    });

    console.log('Check Price 1');
    calculateFinalPrice();
  })

  // === Stückzahl
  $('.quantity__input').on('change', function(){
    productQuantity = $(this).val();
    // update Variant
    checkSurfaceQuantityToggleVariant();
  });

  // === Change AddonPrice Variant from Globo
  document.addEventListener("globo.formbuilder.scripts.loaded", function() {
    console.log('globo loaded');

    // Change Events
    $(document).on('change','.gpo-element input:not([name*="surface="]), .gpo-element select',function(e){
      // console.log(e);
      if ( $(this)[0].hasAttribute('gpo-data-variant-id') ) {
        // get recent values first
        // checkSurfaceQuantityToggleVariant();
        
        // get recent surface
        // let variantArray = recentVariant.split("==");
        // let variantSurfaceValues = variantArray[0];
        
        // get product handle of globo option
        let productHandle = $(this).attr('gpo-data-product-handle');
        
        // Get recent surface from addon products variants list and set new variant
        let addonProduct = $('.addon-products-list').find('.addon-product[data-addon-handle='+productHandle+']').find('.addon-variant[data-variant-title='+recentVariantSurface+']');
        console.log('productHandle: ' + productHandle);
        console.log('recentvariantsurface: ' + recentVariantSurface);
        console.log(addonProduct);
        let variantID = addonProduct.attr('data-variant-id');
        // console.log('variant ID: ' + variantID);
        $(this).attr('gpo-data-variant-id', variantID);
        
        // overwrite label with correct addon price
        let addonLabel = $(this).next('label').find('.addOn');
        let addonPriceValue = addonProduct.attr('data-variant-price');
        const tpl = PriceFmt.templateFromPrice();
        let addonPriceFormatted = PriceFmt.format(parseFloat(addonPriceValue), tpl);
        let newLabel = '(+ ' + addonPriceFormatted + ')';
        console.log(newLabel);
        if ( $(this).is(':checked') ) {
          $(this).parents('.gpo-element:has(:not(input[type=radio][name*="surface=="]))').children('label').find('.addOn .transcy-money').html(addonPriceFormatted);
        }
        addonLabel.html(newLabel);
        // Check if addonProduct is not the first child and pass addon price to calculateFinalPrice
          let addonProductParent = addonProduct.parent();
          let isFirstChild = addonProduct.is(':first-child');

          if (!isFirstChild && $(this).is(':checked')) {
              // Pass addon price to calculateFinalPrice function
              calculateFinalPrice(parseFloat(addonPriceValue));
          } else {
              calculateFinalPrice();
          }
      } else {
          calculateFinalPrice();
      }
    });
  });
});

// Variables
// Aufbau Variante:
// 0-16==25-49
// Fläche ab - Fläche bis == Anzahl ab - Anzahl bis

let productWidth = Number(0);
let productHeight = Number(0);
let productSurface = Number(0);
let productSurfaceComma;
let productQuantity = Number(0);

let surfaceLow = 0;
let surfaceHigh = 0;

let productType = $('.product-type').attr('data-product-type');

let maximumSurface = Number(150);
if ( productType == "Gewebte Label" ) {
  maximumSurface = Number(50);
}
if ( productType == "Schlüsselanhänger" ) {
  maximumSurface = Number(100);
}

if ( productType == "Emaille Pin" ) {
  maximumSurface = Number(25);
}

let minimumQuantity = Number(10);
// Vanilla Shopify Variants
// let firstVariantValue = $('input[name="Surface-Quantity"]').first().val();
// let firstVariantNumber = firstVariantValue.split("==");
// let firstVariantLowestNumber = firstVariantNumber[1].split("-");
// minimumQuantity = firstVariantLowestNumber[0];

// GPO Variants – set in onGpoContainerAdded function
let firstVariantValue;
let firstVariantNumber;
let firstVariantLowestNumber;

// let minimumQuantity = 10;

let defaultQuantity = Number(50);

let singlePrice;
// let addonPrice = 0;
let finalPrice;
$('.finalprice').hide();

var recentVariant = '';
var recentSurfaceValues = '';

// Set default quantity
$('.quantity__input').val(defaultQuantity);

function checkSurfaceQuantityToggleVariant() {
  const surfaceInputs = $('input[name*="surface=="]');
  const quantityInput = $('.quantity__input');
  const quantityButton = $('.quantity__button:first-child');
  const productFormButtons = $('.product-form__buttons');

  if (surfaceInputs.length > 0) {
    // Get recent quantity
    productQuantity = Number(quantityInput.val());

    // Set Minimum if necessary
    if (productQuantity < minimumQuantity) {
      productQuantity = minimumQuantity;
      quantityInput.val(minimumQuantity);
    }

    // Toggle button states based on the quantity
    quantityButton.toggleClass('disabled', productQuantity <= minimumQuantity)
                  .attr('disabled', productQuantity <= minimumQuantity);

    // Initialize matchingVariant
    let matchingVariant = null;

    // Loop to find the matching variant based on surface and quantity
    surfaceInputs.each(function(){
      const variantQuantity = $(this).val();
      const variantSurface = $(this).attr('name').split("==")[1];
      const [variantMinSurface, variantMaxSurface] = variantSurface.split("-").map(s => Number(s.replace(',','.')));
      const [variantMinQuantity, variantMaxQuantity] = variantQuantity.split("-").map(Number);

      if (productSurface >= variantMinSurface && productSurface < variantMaxSurface) {
        // Condition for matching variant
        if ((isNaN(variantMaxQuantity) && productQuantity >= variantMinQuantity) ||
            (productQuantity >= variantMinQuantity && productQuantity < variantMaxQuantity)) {
          matchingVariant = $(this);
          recentVariantQuantity = variantQuantity;
          recentVariantSurface = variantSurface;
        }
      }
    });

    if (matchingVariant) {
      // Uncheck other inputs and check the selected one
      // $('input[name*="surface=="]').prop('checked', false);
      $('input[name*="surface=="]').each(function() {
        const input = $(this);
        if (input.prop('checked')) {
          // Only trigger change event if the input was previously selected
          input.prop('checked', false);
          const changeEvent = new Event('change', { bubbles: true, cancelable: true });
          input[0].dispatchEvent(changeEvent);
        }
      });
      
      matchingVariant.prop('checked', true);

      // Dispatch the change event for the newly selected input
      const changeEvent = new Event('change', { bubbles: true, cancelable: true });
      matchingVariant[0].dispatchEvent(changeEvent);

      // Simulate click for the matching variant if necessary
      matchingVariant.click();

      // Debugging: Ensure the right variant is selected
      console.log('Selected Variant:', recentVariantSurface, recentVariantQuantity);
    } else {
      console.log('No matching variant found for the current surface and quantity.');
    }

    // Deactivate buttons when surface too large
    const isSurfaceTooLarge = productSurface > maximumSurface;
    productFormButtons.css({
      opacity: isSurfaceTooLarge ? '0.2' : '1',
      'pointer-events': isSurfaceTooLarge ? 'none' : 'auto'
    });

    // Trigger price calculation after variant selection
    console.log('Calculating final price...');
    calculateFinalPrice();
  } else {
    console.log('No surface inputs found.');
  }
}


function calculateFinalPrice(addonPrice = 0) {
  setTimeout(function(){
    const handle = PriceFmt.getPriceHandle();
    if (!handle) {
      console.warn('calculateFinalPrice: no price element found');
      return;
    }
    const priceText = handle.read();
    const template = PriceFmt.detectTemplate(priceText);
    singlePrice = PriceFmt.parse(priceText);
    if (isNaN(singlePrice)) singlePrice = 0;
    singlePrice = singlePrice + addonPrice;
    finalPrice = singlePrice * productQuantity;
    handle.write(PriceFmt.format(singlePrice, template));
    $('.finalprice-result').text(PriceFmt.format(finalPrice, template));
    console.log('Einzelpreis: ' + singlePrice);
    console.log('Anzahl: ' + productQuantity);
    console.log('Gesamtpreis: ' + finalPrice);
  }, 800);
}

var setMinimumQuantityCounter = 0;

function onGpoContainerAdded() {
  if ( setMinimumQuantityCounter < 1 ) {
    firstVariantValue = $('input[name*="surface=="]').first().val();
    // console.log('firstVariantValue = ' + firstVariantValue);
    firstVariantNumber = firstVariantValue.split("-");
    firstVariantLowestNumber = firstVariantNumber[0];
    minimumQuantity = firstVariantLowestNumber;

    checkSurfaceQuantityToggleVariant();

    console.log('gpo-container added');
  
    // Copy final single and finale price above cart button
    if ( $('[id*=price-template] + .finalprice').length > 0 ) {
      // console.log($('.gpo-container'));
      $('[id*=price-template], [id*=price-template] + .finalprice, [id*=price-template] + .finalprice + .product__tax').clone().insertBefore('.gpo-container + .product-form__submit');
    }
    
    setMinimumQuantityCounter = 1;
  }
    
}

// Create a mutation observer
const observer = new MutationObserver((mutationsList, observer) => {
    for (let mutation of mutationsList) {
        if (mutation.type === 'childList') {
            // Check if .gpo-container element is added
            const addedNodes = Array.from(mutation.addedNodes);
            const gpoContainer = addedNodes.find(node => node.classList && node.classList.contains('gpo-container'));
            if (gpoContainer) {
                onGpoContainerAdded();      
                // If you only want to observe until the element is added, you can disconnect the observer
                observer.disconnect();
                break;
            }
        }
    }
});

// Configuration of the observer
const config = { childList: true, subtree: true };

// Start observing the document body
observer.observe(document.body, config);